import { sendAnalytics } from '@audio-network/analytics'
import { create } from 'zustand'

import {
	addFavourite,
	clearAll,
	removeFavourite,
} from '@/services/favouritesService'
import { flushCurrentSession } from '@/services/sessionService'
import { sendAlgoliaInsightEvents } from '@/utils/algolia/sendAlgoliaInsightEvents'

interface FavouritesState {
	favouriteTracks: number[]
	setFavouriteTracks: (favouriteTracks: number[]) => void
	toggleFavourite: (track: Track, source: string) => void
	removeFavourites: (tracks: Track[], source: string) => void
	clearAllFavourites: () => void
}

const useFavouritesStore = create<FavouritesState>()((set, get) => ({
	favouriteTracks: [],
	setFavouriteTracks: (favouriteTracks) => {
		set({
			favouriteTracks,
		})
	},
	toggleFavourite: (track, source) => {
		flushCurrentSession()
		const previousFavouriteTracks = get().favouriteTracks
		const isFavourite = previousFavouriteTracks.some(
			(favouriteTrackId) => favouriteTrackId === track.id
		)

		if (isFavourite) {
			set({
				favouriteTracks: previousFavouriteTracks.filter(
					(favouriteTrackId) => favouriteTrackId !== track.id
				),
			})

			void removeFavourite(track.id).then((success) => {
				if (!success) {
					set({ favouriteTracks: previousFavouriteTracks })
					return
				}

				sendAnalytics({
					noun: 'Remove from Favourites',
					pastTenseVerb: 'Clicked',
					eventParameters: {
						TrackTitle: track.title,
						TrackId: track.id,
						AlbumNo: track.album.number,
						TrackSource: source,
						Genre: track.album.genre,
						Catalogue: track.album.catalogue.name,
					},
				})
			})
		} else {
			set({
				favouriteTracks: [...previousFavouriteTracks, track.id],
			})

			void addFavourite(track.id).then((success) => {
				if (!success) {
					set({ favouriteTracks: previousFavouriteTracks })
					return
				}

				sendAnalytics({
					noun: 'Add to Favourites',
					pastTenseVerb: 'Clicked',
					eventParameters: {
						TrackTitle: track.title,
						TrackId: track.id,
						AlbumNo: track.album.number,
						TrackSource: source,
						Genre: track.album.genre,
						Catalogue: track.album.catalogue.name,
					},
				})

				sendAlgoliaInsightEvents({
					eventType:
						source === 'Search'
							? 'convertedObjectIDsAfterSearch'
							: 'convertedObjectIDs',
					eventName: 'Add to Favourites Clicked',
					track: track,
				})
			})
		}
	},
	removeFavourites: (tracks, source) => {
		flushCurrentSession()
		const idsToRemove = new Set(tracks.map(({ id }) => id))
		const previousFavouriteTracks = get().favouriteTracks

		set({
			favouriteTracks: previousFavouriteTracks.filter(
				(favouriteTrackId) => !idsToRemove.has(favouriteTrackId)
			),
		})

		void Promise.all(
			tracks.map(async (track) => ({
				track,
				success: await removeFavourite(track.id),
			}))
		).then((results) => {
			const failedIds = new Set(
				results.filter(({ success }) => !success).map(({ track }) => track.id)
			)

			if (failedIds.size > 0) {
				set({
					favouriteTracks: previousFavouriteTracks.filter(
						(favouriteTrackId) =>
							!idsToRemove.has(favouriteTrackId) ||
							failedIds.has(favouriteTrackId)
					),
				})
			}

			results
				.filter(({ success }) => success)
				.forEach(({ track }) => {
					sendAnalytics({
						noun: 'Remove from Favourites',
						pastTenseVerb: 'Clicked',
						eventParameters: {
							TrackTitle: track.title,
							TrackId: track.id,
							AlbumNo: track.album.number,
							TrackSource: source,
							Genre: track.album.genre,
							Catalogue: track.album.catalogue.name,
						},
					})
				})
		})
	},
	clearAllFavourites: () => {
		flushCurrentSession()
		clearAll().then(() => {
			set(() => ({
				favouriteTracks: [],
			}))
		})
	},
}))
export default useFavouritesStore
